import React, { useState, useEffect } from "react";
import Select from "react-select";
import AsyncSelect from "react-select/async";
import styled from "styled-components";

import microApi from "../helpers/microApi";

const Checkbox = styled.div`
  margin-top: 0.5em;
  padding-left: 1.8em;
  position: relative;
  cursor: pointer;

  &::before {
    border: solid #204354 2px;
    content: "";
    height: 0.9em;
    left: 2px;
    position: absolute;
    top: 50%;
    transform: translateY(-50%);
    width: 0.9em;
    background: ${props => props.checked ? "#204354" : "none"};
  }

  ${props => props.checked ? `
  &::after {
    -ms-transform: translateY(-60%) rotate(38deg);
    -webkit-transform: translateY(-60%) rotate(38deg);
    border-color: #fff;
    border-style: solid;
    border-width: 0 2px 2px 0;
    content: "";
    height: 0.6em;
    left: 8px;
    position: absolute;
    top: 50%;
    transform: translateY(-71%) rotate(45deg);
    width: 0.3em;
  }
  ` : ""}
`;

const customStyles = {
  control: (provided, state) => ({
    ...provided,
    fontSize: state.selectProps.large ? "2em" : "1em",
    fontWeight: state.selectProps.large ? "bold" : "normal",
    borderWidth: "0 0 2px 0 !important",
    marginBottom: "1rem",
    boxShadow: "0 !important"
  }),
  valueContainer: (provided, state) => ({
    ...provided,
    paddingLeft: 0
  }),
  indicatorSeparator: (provided, state) => ({
    display: "none"
  }),
  indicatorsContainer: (provided, state) => ({
    ...provided,
    marginRight: -8
  })
};

const customTheme = theme => ({
  ...theme,
  borderRadius: 0,
  colors: {
    ...theme.colors,
    primary: "#204354"
  }
});

const noOptionsMessage = ({ inputValue }) => inputValue
  ? "No results"
  : "Type to search"

const Label = styled.div`
  text-transform: uppercase;
  font-size: 0.8em;
  font-weight: bold;
  margin-top: ${props => props.first ? 0 : "2em"};
`;

const getOptionLoader = (taxonomy, nation) => async q => {
  const res = await microApi.get(`/${nation}/taxonomies/${taxonomy}?q=${q}${nation === "us" ? "&soc_version=soc_emsi_2019" : ""}`);
  return res.data.data.map(i => ({ value: i.id, label: i.name }));
};

const filters = {
  us: {
    region: "state",
    industry: "naics3",
    occupation: "soc5"
  },
  ca: {
    region: "province",
    industry: "naics3",
    occupation: "noc4"
  }
}

const Filters = ({ setFilters, nation }) => {
  const [regionOptions, setRegionOptions] = useState([{
    value: 0, label: {
      us: "United States",
      ca: "Canada"
    }[nation]
  }]);
  const [region, setRegion] = useState(regionOptions[0]);
  const [industry, setIndustry] = useState([]);
  const [occupation, setOccupation] = useState([]);
  const [company, setCompany] = useState([]);
  const [title, setTitle] = useState([]);
  const [skills, setSkills] = useState([]);
  const [isRemote, setIsRemote] = useState(false);

  useEffect(() => {
    const getStates = async () => {
      const res = await microApi.get(`/${nation}/taxonomies/${filters[nation].region}?limit=100`);
      setRegionOptions(r => r.concat(res.data.data.map(s => ({
        value: s.id,
        label: s.name
      }))));
    };
    getStates();
  }, [nation]);

  useEffect(() => {
    const filter = {};
    filter[filters[nation].region] = [region.value].filter(Boolean);
    filter[filters[nation].industry] = (industry || []).map(n => n.value);
    filter[filters[nation].occupation] = (occupation || []).map(n => n.value);
    filter.company = (company || []).map(n => n.value);
    filter.title = (title || []).map(n => n.value);
    filter.skills = (skills || []).map(n => n.value);
    for (let f in filter) {
      if (!filter[f].length) {
        delete filter[f];
      }
    }
    if (isRemote) {
      filter.is_remote = true;
    }
    setFilters(filter);
  }, [region, industry, occupation, company, title, skills, setFilters, nation, isRemote]);

  return (
    <div>
      <div style={{ position: "sticky", top: 10 }}>
        <Label first>Region</Label>
        <Select
          styles={customStyles}
          theme={customTheme}
          noOptionsMessage={noOptionsMessage}
          value={region}
          onChange={setRegion}
          options={regionOptions}
          placeholder="Choose region"
          large
        />
        <Label>Industry</Label>
        <AsyncSelect
          styles={customStyles}
          theme={customTheme}
          noOptionsMessage={noOptionsMessage}
          isMulti
          cacheOptions
          placeholder="Search..."
          value={industry}
          onChange={setIndustry}
          loadOptions={getOptionLoader(filters[nation].industry, nation)}
        />
        <Label>Occupation</Label>
        <AsyncSelect
          styles={customStyles}
          theme={customTheme}
          noOptionsMessage={noOptionsMessage}
          isMulti
          cacheOptions
          placeholder="Search..."
          value={occupation}
          onChange={setOccupation}
          loadOptions={getOptionLoader(filters[nation].occupation, nation)}
        />
        <Label>Company</Label>
        <AsyncSelect
          styles={customStyles}
          theme={customTheme}
          noOptionsMessage={noOptionsMessage}
          isMulti
          cacheOptions
          placeholder="Search..."
          value={company}
          onChange={setCompany}
          loadOptions={getOptionLoader("company", nation)}
        />
        <Label>Job Title</Label>
        <AsyncSelect
          styles={customStyles}
          theme={customTheme}
          noOptionsMessage={noOptionsMessage}
          isMulti
          cacheOptions
          placeholder="Search..."
          value={title}
          onChange={setTitle}
          loadOptions={getOptionLoader("title", nation)}
        />
        <Label>Skills</Label>
        <AsyncSelect
          styles={customStyles}
          theme={customTheme}
          noOptionsMessage={noOptionsMessage}
          isMulti
          cacheOptions
          placeholder="Search..."
          value={skills}
          onChange={setSkills}
          loadOptions={getOptionLoader("skills", nation)}
        />
        <Label>Remote Jobs</Label>
        <Checkbox checked={isRemote} onClick={() => setIsRemote(!isRemote)}>Limit to remote-friendly jobs</Checkbox>
      </div>
    </div>
  );
};

export default Filters;